import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import {
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  EnvelopeIcon,
  PhoneIcon,
  BriefcaseIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  NoSymbolIcon,
  LockClosedIcon,
  LockOpenIcon,
  ShieldExclamationIcon,
  CurrencyRupeeIcon,
  BanknotesIcon,
  DocumentTextIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/apiError';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle response errors — surface backend messages via userMessage
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      toast.error('Session expired. Please login again.');
    }
    error.userMessage = getApiErrorMessage(error, 'Something went wrong. Please try again.');
    return Promise.reject(error);
  }
);

// Shimmer loading animation
const Shimmer = () => (
  <motion.div
    className="absolute inset-0 -translate-x-full"
    style={{
      background:
        'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.3), transparent)',
    }}
    animate={{
      x: ['0%', '200%'],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, trend, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
    className="relative overflow-hidden bg-white/70 backdrop-blur-lg rounded-xl shadow-sm border border-white/20 p-6 hover:shadow-xl transition-all"
  >
    {loading ? (
      <div className="animate-pulse">
        <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 mb-3"></div>
        <div className="h-8 bg-gradient-to-r from-blue-200 to-violet-200 rounded w-16"></div>
      </div>
    ) : (
      <>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">{title}</p>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
              {value}
            </h3>
            {trend && (
              <p className="text-xs text-green-600 mt-2 flex items-center font-medium">
                <span className="mr-1">↑</span> {trend}
              </p>
            )}
          </div>
          <div
            className={`p-3 rounded-xl bg-gradient-to-br ${color} shadow-lg`}
          >
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </>
    )}
  </motion.div>
);

// Loading Skeleton
const TableSkeleton = () => (
  <div className="space-y-3 p-6">
    {[...Array(5)].map((_, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.1 }}
        className="relative overflow-hidden bg-gradient-to-r from-blue-50/50 to-violet-50/50 rounded-lg h-16"
      >
        <Shimmer />
      </motion.div>
    ))}
  </div>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-blue-50/30 to-violet-50/30">
      <div className="text-xs text-gray-600">
        Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
        <span className="font-semibold text-gray-900">{totalPages}</span>
      </div>

      <div className="flex items-center space-x-2">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg transition-all ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-500 hover:to-violet-500 hover:text-white shadow-sm'
          }`}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </motion.button>

        {getPageNumbers().map((page, index) =>
          page === '...' ? (
            <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
              ...
            </span>
          ) : (
            <motion.button
              key={page}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                currentPage === page
                  ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {page}
            </motion.button>
          )
        )}

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg transition-all ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-500 hover:to-violet-500 hover:text-white shadow-sm'
          }`}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </motion.button>
      </div>
    </div>
  );
};

// ── Helper: check if an account is currently brute-force locked ───────────────
function isAccountLocked(member) {
  return member.lockUntil && new Date(member.lockUntil) > new Date();
}

function lockMinutesRemaining(member) {
  if (!isAccountLocked(member)) return 0;
  return Math.ceil((new Date(member.lockUntil) - Date.now()) / 60000);
}

// ── Salary History Table ────────────────────────────────────────────────────
const SalaryHistory = ({ payments, loadingPayments, onDelete, isAdmin }) => {
  if (loadingPayments) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400 text-sm gap-2">
        <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading payment history…
      </div>
    );
  }
  if (!payments.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-gray-400">
        <BanknotesIcon className="h-10 w-10 mb-2 opacity-40" />
        <p className="text-sm">No salary records yet.</p>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-blue-50 to-violet-50 text-xs text-gray-500 uppercase">
            <th className="px-4 py-2 text-left font-semibold">Date</th>
            <th className="px-4 py-2 text-left font-semibold">Amount</th>
            <th className="px-4 py-2 text-left font-semibold">Mode</th>
            <th className="px-4 py-2 text-left font-semibold">Notes / Ref</th>
            {isAdmin && <th className="px-4 py-2 text-center font-semibold">Action</th>}
          </tr>
        </thead>
        <tbody>
          {payments.map((p, i) => (
            <tr key={p._id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
              <td className="px-4 py-2 whitespace-nowrap">
                {p.date ? new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </td>
              <td className="px-4 py-2 font-semibold text-emerald-700 whitespace-nowrap">
                ₹{(p.amount || 0).toLocaleString('en-IN')}
              </td>
              <td className="px-4 py-2 capitalize whitespace-nowrap">
                {(p.paymentMethod || 'cash').replace(/_/g, ' ')}
              </td>
              <td className="px-4 py-2 text-gray-600 max-w-xs truncate">
                {p.description || '—'}
              </td>
              {isAdmin && (
                <td className="px-4 py-2 text-center">
                  <button
                    onClick={() => onDelete(p._id)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    title="Delete record"
                  >
                    <TrashIcon className="h-4 w-4 inline" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Staff Detail View Component
const StaffDetailView = ({ staff, onBack, onEdit, onDelete, onToggleActive, onUnlock, isAdmin, salaryProps }) => {
  const locked = isAccountLocked(staff);
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <motion.button
          whileHover={{ scale: 1.02, x: -5 }}
          whileTap={{ scale: 0.98 }}
          onClick={onBack}
          className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-white/70 backdrop-blur-lg border border-white/20 text-gray-700 hover:shadow-lg transition-all"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span className="font-medium">Back to Staff</span>
        </motion.button>

        {isAdmin && (
          <div className="flex items-center flex-wrap gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onEdit}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-violet-500 text-white font-medium shadow-lg shadow-blue-500/30 hover:shadow-xl transition-all"
            >
              <PencilSquareIcon className="h-5 w-5 inline mr-2" />
              Edit
            </motion.button>
            {locked && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onUnlock}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium shadow-lg shadow-orange-500/30 hover:shadow-xl transition-all flex items-center gap-2"
              >
                <LockOpenIcon className="h-5 w-5" />
                Unlock Account
              </motion.button>
            )}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onToggleActive}
              className={`px-4 py-2 rounded-xl text-white font-medium shadow-lg hover:shadow-xl transition-all ${
                staff.isActive
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/30'
                  : 'bg-gradient-to-r from-green-500 to-emerald-500 shadow-green-500/30'
              }`}
            >
              {staff.isActive ? (
                <><NoSymbolIcon className="h-5 w-5 inline mr-2" />Deactivate</>
              ) : (
                <><CheckIcon className="h-5 w-5 inline mr-2" />Activate</>
              )}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onDelete}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-medium shadow-lg shadow-red-500/30 hover:shadow-xl transition-all"
            >
              <TrashIcon className="h-5 w-5 inline mr-2" />
              Delete
            </motion.button>
          </div>
        )}
      </div>

      {/* Lock warning banner */}
      {locked && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-4 bg-rose-50 border border-rose-200 rounded-2xl px-6 py-4"
        >
          <div className="flex-shrink-0 p-2 rounded-xl bg-rose-100">
            <ShieldExclamationIcon className="h-6 w-6 text-rose-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-800">Account Locked — Brute-Force Protection</p>
            <p className="text-xs text-rose-600 mt-0.5">
              This account has been temporarily locked after too many failed login attempts.
              It will unlock automatically in <strong>{lockMinutesRemaining(staff)} minute{lockMinutesRemaining(staff) !== 1 ? 's' : ''}</strong>, or an admin can unlock it immediately.
            </p>
          </div>
          {isAdmin && (
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={onUnlock}
              className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shadow-md transition-all"
            >
              <LockOpenIcon className="h-4 w-4" />
              Unlock Now
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Staff Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden"
      >
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-blue-500 to-violet-500 p-8">
          <div className="flex items-center space-x-6">
            <div className="h-24 w-24 rounded-full bg-white/20 backdrop-blur-lg border-4 border-white/30 flex items-center justify-center text-white font-bold text-3xl shadow-xl">
              {staff.name.charAt(0)}
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">
                {staff.name}
              </h2>
              <div className="flex items-center space-x-4">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-lg ${
                    staff.isActive
                      ? 'bg-green-500/20 text-white border border-green-300/30'
                      : 'bg-gray-500/20 text-white border border-gray-300/30'
                  }`}
                >
                  {staff.isActive ? (
                    <>
                      <span className="h-2 w-2 bg-white rounded-full mr-2"></span>
                      Active
                    </>
                  ) : (
                    <>
                      <span className="h-2 w-2 bg-gray-300 rounded-full mr-2"></span>
                      Inactive
                    </>
                  )}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-lg text-white border border-white/30 capitalize">
                  <BriefcaseIcon className="h-3 w-3 mr-1" />
                  {staff.role}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Details Grid */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Contact Information
              </h3>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <EnvelopeIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Email</p>
                  <p className="text-sm text-gray-900 font-medium">
                    {staff.email}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <PhoneIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Phone</p>
                  <p className="text-sm text-gray-900 font-medium">
                    {staff.phone}
                  </p>
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Employment Details
              </h3>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    Joined Date
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {new Date(staff.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <UserIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    Created By
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {staff.createdBy?.name || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Account Information
              </h3>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">
                    Last Updated
                  </p>
                  <p className="text-sm text-gray-900 font-medium">
                    {new Date(staff.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              {staff.lastLogin && (
                <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                    <CheckIcon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium">
                      Last Login
                    </p>
                    <p className="text-sm text-gray-900 font-medium">
                      {new Date(staff.lastLogin).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              )}

              {/* Lock status */}
              <div className={`flex items-start space-x-3 p-4 rounded-xl ${locked ? 'bg-rose-50 border border-rose-100' : 'bg-gradient-to-r from-blue-50 to-violet-50'}`}>
                <div className={`p-2 rounded-lg ${locked ? 'bg-rose-100' : 'bg-gradient-to-br from-blue-500 to-violet-500'}`}>
                  <LockClosedIcon className={`h-4 w-4 ${locked ? 'text-rose-600' : 'text-white'}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Login Lock Status</p>
                  {locked ? (
                    <>
                      <p className="text-sm font-semibold text-rose-700">Locked</p>
                      <p className="text-xs text-rose-500 mt-0.5">
                        Unlocks in {lockMinutesRemaining(staff)} min
                        {' '}·{' '}
                        {new Date(staff.lockUntil).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-green-700 font-medium">Unlocked</p>
                  )}
                </div>
              </div>
            </div>

            {/* System Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                System Information
              </h3>

              <div className="flex items-start space-x-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-violet-50">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-violet-500">
                  <UserIcon className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">User ID</p>
                  <p className="text-sm text-gray-900 font-mono">{staff._id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Salary / Payment History ─────────────────────────────── */}
      {salaryProps && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden"
        >
          {/* Section Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-white/20">
                <BanknotesIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Salary Records</h3>
                <p className="text-xs text-emerald-100">
                  Total Paid:{' '}
                  <span className="font-semibold">
                    ₹{salaryProps.totalPaid.toLocaleString('en-IN')}
                  </span>
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={salaryProps.onAddPayment}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-emerald-700 font-semibold text-sm shadow hover:shadow-md transition-all"
              >
                <PlusCircleIcon className="h-4 w-4" />
                Add Salary Record
              </button>
            )}
          </div>

          {/* History Table */}
          <div className="p-4">
            <SalaryHistory
              payments={salaryProps.payments}
              loadingPayments={salaryProps.loading}
              onDelete={isAdmin ? salaryProps.onDeletePayment : undefined}
              isAdmin={isAdmin}
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default function StaffManagement({ user }) {
  usePageMeta(PAGE_META.staff);
  const isAdmin = user?.role === 'admin';
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingStaff, setSubmittingStaff] = useState(false);
  const [deletingStaff, setDeletingStaff] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalStaff, setTotalStaff] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'counselor',
    password: '',
  });

  // ── Salary / Payment state ──────────────────────────────────────────────
  const [salaryPayments, setSalaryPayments]       = useState([]);
  const [salaryTotalPaid, setSalaryTotalPaid]     = useState(0);
  const [loadingSalary, setLoadingSalary]         = useState(false);
  const [showSalaryModal, setShowSalaryModal]     = useState(false);
  const [recordingSalary, setRecordingSalary]     = useState(false);
  const [uploadingSlip, setUploadingSlip]         = useState(false);
  const slipInputRef = useRef(null);
  const [salaryForm, setSalaryForm] = useState({
    amount:      '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'cash',
    referenceNo: '',
    notes:       '',
    paymentSlip: '',
  });

  const ITEMS_PER_PAGE = 15;

  // Fetch staff members
  const fetchStaff = async (page = 1) => {
    try {
      setLoading(true);
      const response = await api.get(`/auth?page=${page}`);
      setStaff(response.data.users);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalStaff(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching staff:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load staff members'));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff(currentPage);
  }, [currentPage]);

  // Filter staff based on search
  const filteredStaff = staff.filter(
    (member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleExportStaff = () => {
    const headers = ['Name', 'Email', 'Phone', 'Role', 'Status', 'Last Login', 'Created Date'];
    const rows = filteredStaff.map((m) => [
      m.name  || '-',
      m.email || '-',
      m.phone || '-',
      m.role  || '-',
      m.isActive ? 'Active' : 'Inactive',
      m.lastLogin  ? formatDateForCsv(m.lastLogin)  : 'Never',
      m.createdAt  ? formatDateForCsv(m.createdAt)  : '-',
    ]);
    downloadCsv(headers, rows, `staff-export-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} staff records`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add new staff member
  const handleAddStaff = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmittingStaff(true);
    try {
      await api.post('/auth/register', formData);
      toast.success('Staff member added successfully!');
      setShowAddModal(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: 'counselor',
        password: '',
      });
      fetchStaff(currentPage);
    } catch (error) {
      console.error('Error adding staff:', error);
      toast.error(getApiErrorMessage(error, 'Failed to add staff member'));
    } finally {
      setSubmittingStaff(false);
    }
  };

  // Edit staff member
  const handleEditStaff = async () => {
    setSubmittingStaff(true);
    try {
      await api.put(`/auth/${selectedStaff._id}`, formData);
      toast.success('Staff member updated successfully!');
      setShowEditModal(false);
      setSelectedStaff(null);
      fetchStaff(currentPage);
    } catch (error) {
      console.error('Error updating staff:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update staff member'));
    } finally {
      setSubmittingStaff(false);
    }
  };

  // Delete staff member
  const handleDeleteStaff = async () => {
    setDeletingStaff(true);
    try {
      await api.delete(`/auth/${selectedStaff._id}`);
      toast.success('Staff member deleted successfully!');
      setShowDeleteModal(false);
      setSelectedStaff(null);
      setShowDetailView(false);
      fetchStaff(currentPage);
    } catch (error) {
      console.error('Error deleting staff:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete staff member'));
    } finally {
      setDeletingStaff(false);
    }
  };

  // Toggle active/inactive for a staff member
  const handleToggleActive = async (member) => {
    try {
      const response = await api.patch(`/auth/${member._id}/toggle-active`);
      toast.success(response.data.message || `Staff member ${member.isActive ? 'deactivated' : 'activated'} successfully!`);
      fetchStaff(currentPage);
      // Refresh detail view data if open
      if (showDetailView && selectedStaff?._id === member._id) {
        setSelectedStaff(prev => ({ ...prev, isActive: !prev.isActive, loginAttempts: 0, lockUntil: null }));
      }
    } catch (error) {
      console.error('Error toggling staff status:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update staff status'));
    }
  };

  // Unlock a brute-force locked account
  const handleUnlockAccount = async (member) => {
    try {
      const response = await api.patch(`/auth/${member._id}/unlock`);
      toast.success(response.data.message || 'Account unlocked successfully!');
      fetchStaff(currentPage);
      // Update the detail view immediately without needing a full reload
      if (showDetailView && selectedStaff?._id === member._id) {
        setSelectedStaff(prev => ({ ...prev, loginAttempts: 0, lockUntil: null }));
      }
    } catch (error) {
      console.error('Error unlocking account:', error);
      toast.error(getApiErrorMessage(error, 'Failed to unlock account'));
    }
  };

  const openEditModal = (member) => {
    setSelectedStaff(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone,
      role: member.role,
      isActive: member.isActive,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (member) => {
    setSelectedStaff(member);
    setShowDeleteModal(true);
  };

  const openDetailView = (member) => {
    setSelectedStaff(member);
    setShowDetailView(true);
    fetchSalaryPayments(member._id);
  };

  // ── Salary helpers ────────────────────────────────────────────────────────
  const fetchSalaryPayments = async (staffId) => {
    setLoadingSalary(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(
        `${API_BASE_URL}/expenses?category=salaries&staffId=${staffId}&limit=200&sortBy=date&sortOrder=desc`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch salary records');
      const json = await res.json();
      const payments = json.data || [];
      setSalaryPayments(payments);
      setSalaryTotalPaid(payments.reduce((s, p) => s + (p.amount || 0), 0));
    } catch {
      toast.error('Failed to load salary history');
    } finally {
      setLoadingSalary(false);
    }
  };

  const handleRecordSalary = async (e) => {
    e.preventDefault();
    if (!salaryForm.amount || Number(salaryForm.amount) <= 0) {
      toast.error('Please enter a valid amount.');
      return;
    }
    const MODE_MAP = {
      cash: 'cash', bank_transfer: 'online_transfer',
      cheque: 'cheque', upi: 'upi', other: 'other',
    };
    setRecordingSalary(true);
    try {
      const token = localStorage.getItem('admin_token');
      const parts = [salaryForm.notes, salaryForm.referenceNo ? `Ref: ${salaryForm.referenceNo}` : ''].filter(Boolean);
      const payload = {
        title:         `Staff Salary — ${selectedStaff.name}`,
        category:      'salaries',
        staffId:       selectedStaff._id,
        amount:        Number(salaryForm.amount),
        date:          salaryForm.paymentDate,
        paymentMethod: MODE_MAP[salaryForm.paymentMode] || 'cash',
        description:   parts.join(' | ') || undefined,
        ...(salaryForm.paymentSlip ? { paymentSlip: salaryForm.paymentSlip } : {}),
      };
      const res = await fetch(`${API_BASE_URL}/expenses`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Failed to record salary');
      }
      toast.success('Salary payment recorded successfully.');
      setShowSalaryModal(false);
      setSalaryForm({ amount: '', paymentDate: new Date().toISOString().split('T')[0], paymentMode: 'cash', referenceNo: '', notes: '', paymentSlip: '' });
      await fetchSalaryPayments(selectedStaff._id);
    } catch (err) {
      toast.error(err.message || 'Failed to record salary');
    } finally {
      setRecordingSalary(false);
    }
  };

  const handleDeleteSalaryPayment = async (paymentId) => {
    if (!window.confirm('Delete this salary record?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/expenses/${paymentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Salary record deleted.');
      await fetchSalaryPayments(selectedStaff._id);
    } catch {
      toast.error('Failed to delete salary record');
    }
  };

  const handleSlipUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploadingSlip(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/storage/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (data.url) {
        setSalaryForm(f => ({ ...f, paymentSlip: data.url }));
        toast.success('Slip uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploadingSlip(false);
    }
  };

  const stats = {
    total:     totalStaff,
    active:    staff.filter((s) => s.isActive).length,
    counselors: staff.filter((s) => s.role === 'counselor').length,
    locked:    staff.filter((s) => isAccountLocked(s)).length,
  };

  // If detail view is shown, render it
  if (showDetailView && selectedStaff) {
    return (
      <div className="space-y-6">
        <StaffDetailView
          staff={selectedStaff}
          onBack={() => setShowDetailView(false)}
          onEdit={isAdmin ? () => { setShowDetailView(false); openEditModal(selectedStaff); } : null}
          onToggleActive={isAdmin ? () => handleToggleActive(selectedStaff) : null}
          onUnlock={isAdmin ? () => handleUnlockAccount(selectedStaff) : null}
          onDelete={isAdmin ? () => { openDeleteModal(selectedStaff); } : null}
          isAdmin={isAdmin}
          salaryProps={{
            payments:       salaryPayments,
            loading:        loadingSalary,
            totalPaid:      salaryTotalPaid,
            onAddPayment:   () => setShowSalaryModal(true),
            onDeletePayment: handleDeleteSalaryPayment,
          }}
        />

        {/* ── Salary Payment Modal ─────────────────────────────────── */}
        {showSalaryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-white/20">
                    <CurrencyRupeeIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">Record Salary Payment</h2>
                    <p className="text-xs text-emerald-100">{selectedStaff.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSalaryModal(false)}
                  className="p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-all"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleRecordSalary} className="p-6 space-y-4 popup-scroll overflow-y-auto max-h-[70vh]">
                {/* Amount + Mode */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Amount (₹) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      placeholder="e.g. 15000"
                      value={salaryForm.amount}
                      onChange={e => setSalaryForm(f => ({ ...f, amount: e.target.value }))}
                      required
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Payment Mode <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={salaryForm.paymentMode}
                      onChange={e => setSalaryForm(f => ({ ...f, paymentMode: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="cheque">Cheque</option>
                      <option value="upi">UPI</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Date + Reference */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Payment Date</label>
                    <input
                      type="date"
                      value={salaryForm.paymentDate}
                      onChange={e => setSalaryForm(f => ({ ...f, paymentDate: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Reference No.</label>
                    <input
                      type="text"
                      placeholder="TXN ID / Cheque No."
                      value={salaryForm.referenceNo}
                      onChange={e => setSalaryForm(f => ({ ...f, referenceNo: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                  <textarea
                    rows={2}
                    placeholder="e.g. Salary for March 2026"
                    value={salaryForm.notes}
                    onChange={e => setSalaryForm(f => ({ ...f, notes: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                  />
                </div>

                {/* Payment Slip */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Payment Slip / Proof (optional)</label>
                  <input
                    ref={slipInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={e => handleSlipUpload(e.target.files[0])}
                  />
                  {salaryForm.paymentSlip ? (
                    <div className="flex items-center gap-3 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <DocumentTextIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                      <a
                        href={salaryForm.paymentSlip}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-emerald-700 underline truncate flex-1"
                      >
                        View uploaded slip
                      </a>
                      <button
                        type="button"
                        onClick={() => { setSalaryForm(f => ({ ...f, paymentSlip: '' })); if (slipInputRef.current) slipInputRef.current.value = ''; }}
                        className="text-xs text-red-500 hover:text-red-700 flex-shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={uploadingSlip}
                      onClick={() => slipInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-60"
                    >
                      {uploadingSlip ? (
                        <>
                          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Uploading…
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Upload slip or proof (PDF / image)
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Footer buttons */}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowSalaryModal(false)}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={recordingSalary}
                    className="px-5 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold shadow hover:shadow-md transition-all disabled:opacity-60 flex items-center gap-2"
                  >
                    {recordingSalary ? (
                      <>
                        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      <>
                        <CurrencyRupeeIcon className="h-4 w-4" />
                        Record Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
            Staff Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage your counselors and administrative members
          </p>
        </motion.div>

        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportStaff}
                disabled={filteredStaff.length === 0}
                className="text-xs gap-1.5 border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                ↓ Export CSV
              </Button>
              <Button
                onClick={() => setShowAddModal(true)}
                className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-lg shadow-blue-500/30"
              >
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Add Staff Member
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          title="Total Staff"
          value={stats.total}
          icon={UserIcon}
          color="from-blue-500 to-blue-600"
          loading={loading}
        />
        <StatsCard
          title="Active Members"
          value={stats.active}
          icon={CheckIcon}
          color="from-green-500 to-green-600"
          loading={loading}
        />
        <StatsCard
          title="Counselors"
          value={stats.counselors}
          icon={BriefcaseIcon}
          color="from-violet-500 to-violet-600"
          loading={loading}
        />
        <StatsCard
          title="Locked Accounts"
          value={stats.locked}
          icon={LockClosedIcon}
          color={stats.locked > 0 ? 'from-rose-500 to-red-600' : 'from-gray-400 to-gray-500'}
          loading={loading}
        />
      </div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className="bg-white/70 backdrop-blur-lg rounded-xl shadow-sm border border-white/20 p-4"
      >
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search staff by name, email, or role..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white/50"
          />
        </div>
      </motion.div>

      {/* Staff Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-white/70 backdrop-blur-lg rounded-xl shadow-sm border border-white/20 overflow-hidden"
      >
        {loading ? (
          <TableSkeleton />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-500/10 to-violet-500/10 border-b border-gray-200/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white  uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white  uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-white  uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100/50">
                  <AnimatePresence>
                    {filteredStaff.map((member, index) => (
                      <motion.tr
                        key={member._id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.05 }}
                        className="hover:bg-blue-50/30 transition-colors cursor-pointer"
                        onClick={() => openDetailView(member)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-semibold text-sm shadow-lg mr-3">
                              {member.name.charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {member.name}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {new Date(member.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <p className="text-xs text-gray-900 flex items-center">
                              <EnvelopeIcon className="h-3 w-3 mr-2 text-gray-400" />
                              {member.email}
                            </p>
                            <p className="text-xs text-gray-600 flex items-center">
                              <PhoneIcon className="h-3 w-3 mr-2 text-gray-400" />
                              {member.phone}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-100 to-violet-100 text-blue-700 capitalize">
                            <BriefcaseIcon className="h-3 w-3 mr-1" />
                            {member.role}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1.5">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium w-fit ${
                                member.isActive
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {member.isActive ? (
                                <>
                                  <span className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                  Active
                                </>
                              ) : (
                                <>
                                  <span className="h-2 w-2 bg-gray-500 rounded-full mr-2"></span>
                                  Inactive
                                </>
                              )}
                            </span>
                            {isAccountLocked(member) && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 w-fit">
                                <LockClosedIcon className="h-3 w-3" />
                                Locked · {lockMinutesRemaining(member)}m
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className="flex items-center space-x-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => openDetailView(member)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </motion.button>
                            {isAdmin && (
                              <>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => openEditModal(member)}
                                  className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <PencilSquareIcon className="h-4 w-4" />
                                </motion.button>
                                {isAccountLocked(member) && (
                                  <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => handleUnlockAccount(member)}
                                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                    title="Unlock Account"
                                  >
                                    <LockOpenIcon className="h-4 w-4" />
                                  </motion.button>
                                )}
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => handleToggleActive(member)}
                                  className={`p-2 rounded-lg transition-colors ${
                                    member.isActive
                                      ? 'text-amber-600 hover:bg-amber-50'
                                      : 'text-green-600 hover:bg-green-50'
                                  }`}
                                  title={member.isActive ? 'Deactivate' : 'Activate'}
                                >
                                  {member.isActive
                                    ? <NoSymbolIcon className="h-4 w-4" />
                                    : <CheckIcon className="h-4 w-4" />
                                  }
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                  onClick={() => openDeleteModal(member)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </motion.button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
              {filteredStaff.length === 0 && (
                <div className="text-center py-12">
                  <BriefcaseIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-500">No staff members found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
            )}
          </>
        )}
      </motion.div>

      {/* Add Staff Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 2 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 18 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-y-auto overflow-x-hidden popup-scroll"
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 p-8 rounded-t-2xl z-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">
                    Add New Staff Member
                  </h3>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="counselor">Counselor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    onClick={() => setShowAddModal(false)}
                    variant="outline"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddStaff}
                    disabled={submittingStaff}
                    className="px-6 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"
                  >
                    {submittingStaff ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                        Adding...
                      </span>
                    ) : 'Add Staff Member'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Staff Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-y-auto overflow-x-hidden popup-scroll"
            >
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-violet-600 p-6 rounded-t-2xl z-10">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">
                    Edit Staff Member
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role *
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="counselor">Counselor</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      name="isActive"
                      value={formData.isActive}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    onClick={() => setShowEditModal(false)}
                    variant="outline"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEditStaff}
                    disabled={submittingStaff}
                    className="px-6 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white"
                  >
                    {submittingStaff ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                        Saving...
                      </span>
                    ) : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
            >
              <div className="p-6">
                <div className="flex items-center justify-center h-14 w-14 rounded-full bg-red-100 mx-auto mb-4">
                  <TrashIcon className="h-7 w-7 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 text-center mb-2">
                  Delete Staff Member
                </h3>
                <p className="text-sm text-gray-600 text-center mb-6">
                  Are you sure you want to delete{' '}
                  <strong>{selectedStaff?.name}</strong>? This action cannot be
                  undone.
                </p>
                <div className="flex justify-center space-x-3">
                  <Button
                    onClick={() => setShowDeleteModal(false)}
                    variant="outline"
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteStaff}
                    disabled={deletingStaff}
                    className="px-6 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                  >
                    {deletingStaff ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                        Deleting...
                      </span>
                    ) : 'Delete'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
